import { ObjectType } from '../Types/base';
import { GenericTranslationLegacy } from '../Types/culture';
import {
	MbfObject,
	LinkedMediabankFile,
	MediabankFile,
	MediabankFileSettings,
	MediabankSettings,
	MediabankUploadLocation,
	MediabankImageSize,
} from '../Types/mediabank';
import { get, getPathWithRootPath, post, upload } from './common';

export type MediabankGetFilesParams = {
	FileTypes?: number[];
	TagIds?: number[];
	GetRelatedFiles?: boolean;
	IncludeViews?: boolean;
	UseComments?: boolean;
	MapLikes?: boolean;
	LoadCommonLocals?: boolean;
	GetDummyContentIfDbEmpty?: boolean;
	JourneyOrder?: boolean;
	ObjectTypes?: ObjectType[];
	ObjectIds?: number[];
	FillSourceUrls?: boolean;
	IncludePrivate?: boolean;
	IncludeInvisible?: boolean;
};

const apiPath = 'api/mediabankuser/';

export async function getFiles(params: MediabankGetFilesParams) {
	const path = `${apiPath}GetMediaBankFilesForUser`;
	const files = await post<MediabankFile[]>(path, params);
	return files;
}

export async function unlinkFileFromObject(fileId: number, objectType: ObjectType, objectId: number) {
	const path = `${apiPath}UnlinkObjectFromMediabankFile?mediabankFileId=${fileId}&objectType=${objectType}&objectId=${objectId}`;
	await post(path);
}

export async function updateNameAndDescription(fileId: number, name: string, description: string) {
	const path = `${apiPath}UpdateNameAndDescriptionForFile?mbfId=${fileId}&name=${name}&desc=${description}`;
	await post(path);
}

export function formatUploadFileFields(
	name: string,
	description: string,
	isPrivate: boolean,
	isInvisible: boolean,
	uploadedFrom: MediabankUploadLocation,
	settings?: MediabankSettings,
	nameTranslations?: GenericTranslationLegacy[],
	descriptionTranslations?: GenericTranslationLegacy[],
	urlRewriteTranslations?: GenericTranslationLegacy[]
) {
	const fields: Record<string, string> = {
		name: name,
		description: description,
		uploadedFrom: uploadedFrom.toString(),
		isPrivate: isPrivate?.toString() ?? 'false',
		isInvisible: isInvisible?.toString() ?? 'false',
		NameTranslations: nameTranslations ? JSON.stringify(nameTranslations) : '',
		DescriptionTranslations: descriptionTranslations ? JSON.stringify(descriptionTranslations) : '',
		urlRewriteTranslations: urlRewriteTranslations ? JSON.stringify(urlRewriteTranslations) : '',
		skipDefaultPermissions: settings?.skipDefaultPermissions?.toString() ?? 'false',
		skipResize: settings?.skipResize?.toString() ?? 'false',
		reuseExistingImage: settings?.reuseExistingImage?.toString() ?? 'false',
		linkToObjectType: settings?.linkToObject?.ObjectType.toString() ?? '',
		linkToObjectId: settings?.linkToObject?.ObjectId?.toString() ?? '',
		linkToDir: settings?.linkToDir ?? '',
		fillSourceUrls: 'true',
		folderId: settings?.folderId?.toString() ?? '',
	};
	return fields;
}

export async function uploadFile(
	file: File,
	name: string,
	description: string,
	isPrivate: boolean,
	isInvisible: boolean,
	uploadedFrom: MediabankUploadLocation,
	settings?: MediabankSettings,
	nameTranslations?: GenericTranslationLegacy[],
	descriptionTranslations?: GenericTranslationLegacy[]
) {
	const fields = formatUploadFileFields(
		name,
		description,
		isPrivate,
		isInvisible,
		uploadedFrom,
		settings,
		nameTranslations,
		descriptionTranslations
	);
	const path = `${apiPath}UploadFile`;
	return upload<MediabankFile>(path, file, fields);
}

export function getThumbPath(path: string) {
	if (!path) return '';

	const fullPath = getPathWithRootPath(path);
	if (fullPath.toLowerCase().indexOf('getfilehandler.ashx') >= 0) {
		const sizeSuffix = fullPath.toLowerCase().includes('thumb=true') ? '' : '&size=sm';
		return `${fullPath}${sizeSuffix}`;
	} else {
		return fullPath;
	}
}

export async function linkExternalFile(
	link: string,
	fileType: number,
	thumbnail: string,
	uploadedFrom: MediabankUploadLocation,
	isPrivate: boolean,
	isInvisible: boolean,
	name: string,
	description: string,
	settings: MediabankFileSettings,
	urlRewrite: string,
	uploadSettings?: MediabankSettings
) {
	const path = `${apiPath}LinkFile`;
	const payload = {
		Link: link,
		Type: fileType,
		Thumbnail: thumbnail,
		UploadedFrom: uploadedFrom.toString(),
		IsPrivate: isPrivate,
		IsInvisible: isInvisible,
		Name: name,
		Description: description,
		Settings: settings,
		UrlRewrite: urlRewrite,
		LinkedObjectId: uploadSettings?.linkToObject?.ObjectId ?? '',
		LinkedObjectType: uploadSettings?.linkToObject?.ObjectType ?? '',
		LinkToDir: uploadSettings?.linkToDir ?? '',
	};

	return await post<MediabankFile>(path, payload);
}

export async function getOneFile(fileId: number, moduleGuid?: string, containerRef?: string) {
	const params = new URLSearchParams({ id: fileId.toString() });
	if (moduleGuid) params.set('moduleGuid', moduleGuid);
	if (containerRef) params.set('containerRef', containerRef);
	const path = `${apiPath}GetOneFile?${params.toString()}`;
	return await get<MediabankFile>(path);
}

export async function getFrontEndObjectLinks(urlFileFilter: string) {
	const path = `${apiPath}GetFrontEndObjectLinksAsync?urlFilter=${urlFileFilter}`;
	return await get<LinkedMediabankFile[]>(path);
}

export const getBackgroundImageStyle = (mbf: MbfObject | null, fallbackImage?: string | null, size?: MediabankImageSize) => {
	if (mbf && mbf.ImageUrl) {
		return {
			backgroundImage: `url(${getPathWithRootPath(mbf.ImageUrl, size)})`,
			backgroundPositionX: mbf.Settings?.FocusX > 0 ? `${mbf.Settings.FocusX}%` : 'center',
			backgroundPositionY: mbf.Settings?.FocusY && mbf.Settings?.FocusY > 0 ? `${mbf.Settings.FocusY}%` : 'center',
		};
	}
	if (fallbackImage) {
		return {
			backgroundImage: `url(${getPathWithRootPath(fallbackImage, size)})`,
			backgroundPosition: 'center',
		};
	}
	return {};
};
